import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

import {
  isAgentLaunchDismissed,
  recordAgentLaunchDismissal,
} from "./agentLaunchDismissal";
import { AgentLaunchModal } from "./AgentLaunchModal";

// Auto-shown agent introduction for users not in the beta; entering names an agent project right here (creation enrolls the workspace in the beta server-side).
export const AgentLaunchPrompt: React.FC = () => {
  const workspace = useCurrentWorkspace();
  if (!workspace) {
    return null;
  }
  // Keyed by workspace so switching workspaces re-runs eligibility.
  return (
    <WorkspaceAgentLaunchPrompt key={workspace.id} workspace={workspace} />
  );
};

const WorkspaceAgentLaunchPrompt: React.FC<{ workspace: Workspace }> = ({
  workspace,
}) => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { createProject } = useOnboardingCreateProject();

  const [hasDeclined, setHasDeclined] = React.useState(false);
  const [isNaming, setIsNaming] = React.useState(false);
  const [projectName, setProjectName] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);

  const isEligible =
    isFeatureEnabled("agent-launch-modal") &&
    workspace.paymentProcessor !== "shopify" &&
    workspace.defaultProjectType !== "agent" &&
    !isAgentLaunchDismissed("popup");

  const isLetterOpen = isEligible && !hasDeclined && !isNaming;

  const handleCreateAgentProject = async () => {
    if (isCreating || !projectName.trim() || !user) {
      return;
    }
    setIsCreating(true);
    try {
      const result = await createProject({
        shopName: projectName.trim(),
        workspaceId: workspace.id,
        hostingType: "replo",
        userEmail: user.email,
        shouldInvalidateUser: true,
        isAgentEnabled: true,
      });
      navigate(
        generatePath(routes.agent.home, { projectId: result.project.id }),
        { replace: true },
      );
    } catch {
      toast.error({
        title: "Couldn't create project",
        description: "Try again. If the problem continues, contact support.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <AgentLaunchModal
        open={isLetterOpen}
        source="dashboard-popup"
        onEnterBeta={() => setIsNaming(true)}
        onDecline={() => {
          recordAgentLaunchDismissal("popup");
          setHasDeclined(true);
        }}
      />
      <ResponsiveDialog
        open={isNaming}
        onOpenChange={(open) => {
          if (!open) {
            setIsNaming(false);
            setHasDeclined(true);
          }
        }}
        title="Name your Replo Agent project"
        size="sm"
        footer={
          <Button
            type="submit"
            form="agent-launch-name-form"
            disabled={isCreating || !projectName.trim()}
            loading={isCreating}
            className="w-full"
          >
            Create Agent project
          </Button>
        }
      >
        <form
          id="agent-launch-name-form"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreateAgentProject();
          }}
        >
          <LabeledControl id="agent-project-name" label="Project" size="base">
            <Input
              id="agent-project-name"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              size="base"
              placeholder="Acme"
              autoFocus
              required
            />
          </LabeledControl>
        </form>
      </ResponsiveDialog>
    </>
  );
};
